import { ApolloProvider } from '@apollo/client';
import { Capacitor } from '@capacitor/core';
import migrationV1 from '@client/migration/v1.ts';
import { DevTools, FormatSimple, Tolgee, TolgeeProvider } from '@tolgee/react';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone'; // dependent on utc plugin
import utc from 'dayjs/plugin/utc';
import { getAnalytics } from 'firebase/analytics';
import { initializeApp } from 'firebase/app';
import { enableMapSet } from 'immer';
import { AuthProvider } from 'provider/src/module/auth/auth-provider.tsx';
import { Provider as ChakraProvider } from 'provider/src/module/chakra-ui/components/ui/provider.tsx';
import { Toaster } from 'provider/src/module/chakra-ui/components/ui/toaster.tsx';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { RouterProvider } from 'react-router-dom';
import { registerSW } from 'virtual:pwa-register';
import { router } from './app/router.tsx';
import { client } from './graphql/client.ts';
import './index.css';

// add this to prompt for a refresh
// const updateSW = registerSW({
//   onNeedRefresh() {
//     if (confirm('New content available. Reload?')) {
//       updateSW(true);
//     }
//   },
// });

registerSW({ immediate: true });

// Setup dayjs
dayjs.extend(utc);
dayjs.extend(timezone);

// Setup Firebase
const firebaseConfig = {
  apiKey: 'AIzaSyC-h59jYQOURCanu5QCJpiy-3FUP16pSDc',
  authDomain: 'inhouse-55263.firebaseapp.com',
  projectId: 'inhouse-55263',
  storageBucket: 'inhouse-55263.appspot.com',
  messagingSenderId: '838641558620',
  appId: '1:838641558620:web:05f61cee220a699096b299',
  measurementId: 'G-JQWSF58XWT',
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Analytics with App Tracking Transparency compliance
if (Capacitor.isNativePlatform()) {
  // On native platforms (iOS/Android), request ATT permission first
  import('./services/app-tracking-transparency-service')
    .then(({ AppTrackingTransparencyService }) => {
      AppTrackingTransparencyService.initializeTracking(
        // On authorized: Initialize Firebase Analytics
        () => {
          console.log('[Firebase] Initializing Analytics with user consent');
          getAnalytics(app);
        },
        // On denied: Skip analytics initialization
        () => {
          console.log('[Firebase] Analytics disabled - user denied tracking');
        },
      ).catch((error) => {
        console.error('[Firebase] Error initializing tracking:', error);
      });
    })
    .catch((error) => {
      console.error('[Firebase] Error loading ATT service:', error);
    });
} else {
  // On web, initialize analytics directly (no ATT required)
  getAnalytics(app);
}

// Setup Sentry

const sentryDns: string | null = import.meta.env.VITE_SENTRY_DNS as string;
if (sentryDns) {
  import('@sentry/browser')
    .then((sentry) => {
      sentry.init({
        dsn: sentryDns,
        release: import.meta.env.VITE_APP_VERSION as string,
        environment: (import.meta.env.VITE_APP_ENV as string | undefined) ?? 'local',
      });
    })
    .catch((error) => {
      /* eslint-disable no-console */
      console.log(error);
    });
}

// Chart setup
ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Immer plugin
enableMapSet();

// Migration
migrationV1();

// Initialize app updater for native platforms
if (Capacitor.isNativePlatform()) {
  import('./services/app-updater-service').then(({ AppUpdaterService }) => {
    AppUpdaterService.getInstance().initialize().catch(console.error);
  });
}


ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const tolgee = Tolgee()
  .use(DevTools())
  .use(FormatSimple())
  .init({
    language: localStorage.getItem('language') ?? 'de',
    staticData: {
      en: () => import('./i18n/en.json').then((m) => m.default),
      de: () => import('./i18n/de.json').then((m) => m.default),
      vi: () => import('./i18n/vi.json').then((m) => m.default),
      pl: () => import('./i18n/pl.json').then((m) => m.default),
    },
  });

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
ReactDOM.createRoot(document.getElementById('root')!).render(
  <HelmetProvider>
    <React.StrictMode>
      <ChakraProvider>
        <TolgeeProvider tolgee={tolgee}>
          <AuthProvider>
            <ApolloProvider client={client}>
              <RouterProvider router={router} />
            </ApolloProvider>
          </AuthProvider>
        </TolgeeProvider>
        <Toaster />
      </ChakraProvider>
    </React.StrictMode>
  </HelmetProvider>,
);
